import React, {useState} from "react"
import {capitalizeFirstLetter} from "../../../services/HelperService"
import {AccountType} from "../../../models/AccountType"
import {ReactComponent as PenBorderSvg} from "../../../assets/svg/pen-border.svg"
import {ReactComponent as PlusSvg} from "../../../assets/svg/plus.svg"
import ModalWidget from "../ModalWidget"
import {useParams} from "react-router-dom";
import SelectField from "../../SelectField";
import {enrollStudentInClassroom} from "../../../services/ClassroomService";
import {useMenu} from "../../../contexts/MenuContext";
import {useClassroom} from "../../../contexts/ClassroomContext";
import {createUserLoadOptions, formatStudentOption} from "../../../utils/selectHelpers";
import {listTerms} from "../../../services/GradebookService";
import {useToast} from "../../../contexts/ToastContext";
import {getApiErrors} from "../../../utils/apiError";

interface AssignStudentProps {
    show?: boolean;
    onHide?: () => void;
    classroomId?: string;
    termId?: string;
    onSuccess?: () => void;
}

interface SelectOption {
    value: string;
    label: string;
}

function AssignStudent({show = true, onHide, classroomId, termId, onSuccess}: AssignStudentProps) {
    const [selectedStudents, setSelectedStudents] = useState<SelectOption[] | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const {navigateTo} = useMenu()
    const {setPendingStudentEnrollment} = useClassroom()
    const {showError, showInfo, showSuccess} = useToast()
    const params = useParams()
    const targetClassroomId = classroomId ?? params.id
    const capitalWord = capitalizeFirstLetter(AccountType.STUDENT) + '(s)'

    const assign = async () => {
        setActionLoading(true)
        try {
            const userIds = selectedStudents?.map(student => student.value);

            if ((userIds?.length ?? 0) === 0) {
                showInfo('Select at least one student to continue.')
                return;
            }

            if (!targetClassroomId) {
                showError('No classroom selected for assignment.')
                return;
            }

            let enrollmentTermId = termId;
            if (!enrollmentTermId) {
                const terms = await listTerms();
                enrollmentTermId = terms.find(term => term.status === 'active')?.id ?? terms[0]?.id;
            }

            if (!enrollmentTermId) {
                showError('No active term is available for student assignment.')
                return;
            }

            // Enroll each selected student in the classroom
            for (const studentId of userIds ?? []) {
                await enrollStudentInClassroom(studentId, targetClassroomId, enrollmentTermId);
            }
            setPendingStudentEnrollment({
                classroomId: targetClassroomId,
                termId: enrollmentTermId,
            });
            showSuccess('Student(s) assigned successfully.');
            onSuccess?.();
            if (onHide) {
                onHide();
            } else {
                navigateTo(-1, false);
            }
        } catch (err: any) {
            showError(getApiErrors(err)._global ?? 'Unable to assign student(s). Please try again.')
        } finally {
            setActionLoading(false)
        }
    };


    return (
        <ModalWidget
            headerIcon={PenBorderSvg}
            show={show}
            title={'Assign ' + capitalWord}
            description={'You can select more than one ' + AccountType.STUDENT + ' at a time.'}
            actionTitle={'Assign ' + capitalWord + ' to Class'}
            actionIcon={PlusSvg}
            actionLoading={actionLoading}
            actionDisabled={(selectedStudents?.length ?? 0) === 0}
            onAction={assign}
            onHide={onHide}
            navigateBack={!onHide}
        >
            <SelectField label={capitalWord + ' Name or ID'}
                        loadOptions={createUserLoadOptions(AccountType.STUDENT)}
                        formatOptionLabel={formatStudentOption}
                        fieldType={AccountType.STUDENT}
                        placeholder="John Doe" onValueChange={setSelectedStudents}/>
        </ModalWidget>
    )
}

export default AssignStudent
